import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useProject } from "@/contexts/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, FileSpreadsheet, FileText, BarChart3, Calendar } from "lucide-react";
import ProjectProgressChart from "@/components/ProjectProgressChart";
import RiskMatrix from "@/components/RiskMatrix";
import { useIsMobile } from "@/hooks/use-mobile";

export default function Reports() {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const [reportPeriod, setReportPeriod] = useState<string>("all");
  const isMobile = useIsMobile();

  if (!currentProject) {
    return <div>Loading...</div>;
  }

  // Calculate overall project progress
  const calculateOverallProgress = () => {
    const totalStages = currentProject.stages.length;
    const completedStages = currentProject.stages.filter((s) => s.status === "completed").length;
    const currentStageProgress = currentProject.stages[currentProject.currentStage]?.progress || 0;
    
    const completeWeight = completedStages / totalStages;
    const currentWeight = 1 / totalStages * (currentStageProgress / 100);
    
    return Math.round((completeWeight + currentWeight) * 100);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      
      <main className="container py-6 flex-1">
        <div className="flex items-center gap-4 mb-6 flex-wrap sm:flex-nowrap">
          <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-3xl font-bold">Project Reports</h1>
            <p className="text-sm text-muted-foreground truncate">
              {currentProject.name} • Client: {currentProject.client}
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="text-base sm:text-lg font-medium">Overall Progress: {calculateOverallProgress()}%</div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Select value={reportPeriod} onValueChange={setReportPeriod}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Report Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="month">Last Month</SelectItem>
                <SelectItem value="quarter">Last Quarter</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
              </SelectContent>
            </Select>
            <Button className="flex items-center gap-2 w-full sm:w-auto">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="summary">
          <div className="overflow-x-auto">
            <TabsList className="mb-6">
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="progress">Progress</TabsTrigger>
              <TabsTrigger value="risks">Risks</TabsTrigger>
              <TabsTrigger value="information">Info Exchange</TabsTrigger>
              <TabsTrigger value="governance">Governance</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="summary">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Project Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-1">
                    {Math.ceil((currentProject.targetCompletionDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    Remaining until completion
                  </div>
                  <div className="text-xs">
                    <div className="flex justify-between mb-1">
                      <span>Start Date</span>
                      <span>{currentProject.startDate.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Target Completion</span>
                      <span>{currentProject.targetCompletionDate.toLocaleDateString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Budget Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-1">
                    £{currentProject.actualCost.toLocaleString()} / £{currentProject.budget.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    {Math.round((currentProject.actualCost / currentProject.budget) * 100)}% of budget used
                  </div>
                  <div className="text-xs">
                    <div className="flex justify-between">
                      <span>Remaining</span>
                      <span>£{(currentProject.budget - currentProject.actualCost).toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Information Exchange
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    let complete = 0;
                    let total = 0;
                    currentProject.stages.forEach(stage => {
                      if (stage.informationExchanges) {
                        total += stage.informationExchanges.length;
                        complete += stage.informationExchanges.filter(
                          item => item.status === "complete" || item.status === "validated"
                        ).length;
                      }
                    });
                    
                    return (
                      <>
                        <div className="text-2xl font-bold mb-1">
                          {complete} / {total}
                        </div>
                        <div className="text-sm text-muted-foreground mb-2">
                          {total > 0 ? Math.round((complete / total) * 100) : 0}% Complete
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Project Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProjectProgressChart project={currentProject} />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Risk Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <RiskMatrix risks={currentProject.risks} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="progress">
            <Card>
              <CardHeader>
                <CardTitle>Stage Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {currentProject.stages.map((stage) => (
                    <div key={stage.id} className="border-b pb-4 last:border-b-0">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                        <h3 className="font-medium">Stage {stage.id}: {stage.name}</h3>
                        <div className="text-sm">
                          {stage.status === "completed" ? (
                            <span className="text-green-600">Completed</span>
                          ) : stage.status === "in-progress" ? (
                            <span className="text-blue-600">{stage.progress}% Complete</span>
                          ) : (
                            <span className="text-gray-500">Not Started</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <div className="h-2 w-full bg-gray-100 rounded-full">
                          <div 
                            className={`h-2 rounded-full ${
                              stage.status === "completed" ? "bg-green-500" : "bg-blue-500"
                            }`}
                            style={{ width: `${stage.progress}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Activities</div>
                          <div>{stage.activities?.length || 0}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Information Items</div>
                          <div>{stage.informationExchanges?.length || 0}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Approvals</div>
                          <div>{stage.approvals?.length || 0}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Date Started</div>
                          <div>{stage.dateStarted ? stage.dateStarted.toLocaleDateString() : "N/A"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="risks">
            <RiskMatrix risks={currentProject.risks} />
          </TabsContent>
          
          <TabsContent value="information">
            <Card>
              <CardHeader>
                <CardTitle>Information Exchange Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {currentProject.stages.map((stage) => (
                    <div key={stage.id} className="border-b pb-4 last:border-b-0">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="font-medium">Stage {stage.id}: {stage.name}</h3>
                        <Button variant="outline" size="sm" onClick={() => navigate(`/stage/${stage.id}`)}>
                          View Details
                        </Button>
                      </div>
                      
                      {stage.informationExchanges && stage.informationExchanges.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {stage.informationExchanges.map((item) => (
                            <div key={item.id} className="flex items-center justify-between border rounded-md p-3">
                              <div className="overflow-hidden">
                                <div className="font-medium truncate">{item.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{item.description}</div>
                              </div>
                              <div className="ml-2 shrink-0">
                                {item.status === "validated" ? (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                    Validated
                                  </span>
                                ) : item.status === "complete" ? (
                                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                    Complete
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full">
                                    Pending
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          No information exchange items for this stage
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="governance">
            <Card>
              <CardHeader>
                <CardTitle>Governance & Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {currentProject.stages.map((stage) => (
                    <div key={stage.id} className="border-b pb-4 last:border-b-0">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="font-medium">Stage {stage.id}: {stage.name}</h3>
                        <div>
                          {stage.status === "completed" ? (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Stage Complete
                            </span>
                          ) : stage.id < currentProject.currentStage ? (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Signed Off
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                              {stage.id === currentProject.currentStage ? "In Progress" : "Not Started"}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {stage.approvals && stage.approvals.length > 0 ? (
                        <div className="space-y-2">
                          {stage.approvals.map((approval) => (
                            <div key={approval.id} className="flex items-center justify-between border rounded-md p-3">
                              <div className="overflow-hidden">
                                <div className="font-medium truncate">{approval.name}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  Approver: {approval.approver}
                                </div>
                              </div>
                              <div className="flex items-center ml-2 shrink-0">
                                <div className="mr-4 text-sm hidden sm:block">
                                  {approval.dateRequested.toLocaleDateString()}
                                </div>
                                {approval.status === "approved" ? (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                    Approved
                                  </span>
                                ) : approval.status === "rejected" ? (
                                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                                    Rejected
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full">
                                    Pending
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          No approvals for this stage
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
